/**
 * WF-Number - Number input component for wizard forms
 *
 * A specialized input for numeric values with min/max/step support.
 *
 * @example
 * <wf-number name="employees" label="Number of Employees" min="1" max="10000" />
 */

import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { WfInput } from './wf-input.js';
import { ValidationController } from '../controllers/ValidationController.js';

@customElement('wf-number')
export class WfNumber extends WfInput {

  /**
   * Minimum value
   */
  @property({ type: Number })
  min?: number;

  /**
   * Maximum value
   */
  @property({ type: Number })
  max?: number;

  /**
   * Step increment
   */
  @property({ type: Number })
  step = 1;

  protected _setupValidators(): void {
    this._validators = [];

    if (this.required) {
      this._validators.push(ValidationController.required());
    }

    // Number range validator
    if (this.min !== undefined || this.max !== undefined) {
      this._validators.push(
        ValidationController.custom(
          'range',
          (value: unknown) => {
            if (value === '' || value === null || value === undefined) {
              return { valid: true }; // Let required handle empty
            }

            const num = Number(value);
            if (isNaN(num)) {
              return { valid: false, error: 'Please enter a valid number' };
            }

            if (this.min !== undefined && num < this.min) {
              return { valid: false, error: `Value must be at least ${this.min}` };
            }

            if (this.max !== undefined && num > this.max) {
              return { valid: false, error: `Value must be at most ${this.max}` };
            }

            return { valid: true };
          },
          'Value out of range'
        )
      );
    }
  }

  render() {
    return html`
      <div class="wf-field-container">
        ${this._renderLabel()}
        <input
          type="number"
          class="wf-input ${this._errorMessage ? 'wf-input-error' : ''}"
          id="${this.name}"
          name="${this.name}"
          placeholder="${this.placeholder}"
          .value="${this._value}"
          ?disabled="${this.disabled}"
          ?required="${this.required}"
          min="${this.min ?? ''}"
          max="${this.max ?? ''}"
          step="${this.step}"
          autocomplete="${this.autocomplete || 'off'}"
          data-testid="wf-input"
          @input="${this._handleInput}"
          @blur="${this._handleBlur}"
          @focus="${this._handleFocus}"
        />
        ${this._renderHint()}
        ${this._renderError()}
      </div>
    `;
  }

  /**
   * Get current value as number
   */
  get valueAsNumber(): number {
    return Number(this._value) || 0;
  }

  /**
   * Set value as number
   */
  set valueAsNumber(val: number) {
    this._value = String(val);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-number': WfNumber;
  }
}
